import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { KeyRound, Lock, CheckCircle2 } from "lucide-react";
import SiteChrome from "@/components/SiteChrome";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";

export default function EliminatoriasResetPassword() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error(t("elim_reset_token_missing"));
      return;
    }
    if (pwd.length < 6) {
      toast.error(t("elim_reset_pwd_min"));
      return;
    }
    if (pwd !== confirm) {
      toast.error(t("elim_profile_pwd_mismatch"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/eliminatoria/reset-password", { token, new_password: pwd });
      setDone(true);
      toast.success(t("elim_reset_pwd_updated"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("elim_reset_fail"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <SiteChrome>
      <section className="max-w-md mx-auto px-6 pt-12 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="rounded-3xl bg-white shadow-xl p-8 border-2 border-violet-100"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="rounded-full bg-violet-600/10 p-2 text-violet-700"><KeyRound className="w-5 h-5" /></div>
            <h1 className="font-[Fredoka] text-2xl font-bold text-slate-900">{t("elim_reset_title")}</h1>
          </div>
          <p className="text-sm text-slate-600 mb-6">{t("elim_reset_subtitle")}</p>

          {done ? (
            <div className="space-y-4 text-center" data-testid="elim-reset-done">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-100 text-emerald-600">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <h2 className="font-[Fredoka] text-xl font-bold text-slate-900">{t("elim_reset_all_good")}</h2>
              <p className="text-sm text-slate-600">{t("elim_reset_done_body")}</p>
              <Button onClick={() => nav("/eliminatorias")} className="w-full bg-violet-600 hover:bg-violet-700 rounded-full font-bold h-11" data-testid="elim-reset-goto-login">
                {t("elim_reset_goto_login")}
              </Button>
            </div>
          ) : !token ? (
            <div className="space-y-4">
              <div className="rounded-2xl bg-rose-50 border-2 border-rose-200 p-4 text-sm text-rose-800" data-testid="elim-reset-no-token">
                {t("elim_reset_no_token_body")}
              </div>
              <Link to="/eliminatorias" className="block text-center text-violet-600 font-bold hover:underline">{t("elim_reset_back")}</Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" autoComplete="off">
              <div>
                <Label className="flex items-center gap-2 mb-1.5"><Lock className="w-4 h-4 text-slate-500" /> {t("elim_profile_new_pwd_label")}</Label>
                <Input data-testid="elim-reset-new" type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder={t("elim_profile_new_pwd_ph")} autoComplete="new-password" />
              </div>
              <div>
                <Label className="flex items-center gap-2 mb-1.5"><Lock className="w-4 h-4 text-slate-500" /> {t("elim_profile_confirm_label")}</Label>
                <Input data-testid="elim-reset-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={t("elim_reset_confirm_ph")} autoComplete="new-password" />
              </div>
              <Button data-testid="elim-reset-submit" type="submit" disabled={loading} className="w-full bg-violet-600 hover:bg-violet-700 rounded-full font-bold h-11">
                {loading ? t("elim_profile_pwd_saving") : t("elim_profile_pwd_save_btn")}
              </Button>
            </form>
          )}
        </motion.div>
      </section>
    </SiteChrome>
  );
}
